(() => {
  const root = document.documentElement;
  if (root.dataset.lurxecareV34Ready === 'true') return;
  root.dataset.lurxecareV34Ready = 'true';

  const collectionEntries = [];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shopAllPath = '/collections/all';
  let revealObserver;
  let cardsMutationObserver;
  let cardsMutationRoot;
  let revealSequence = 0;
  let confirmationTimer;
  let latestAddTrigger;

  const currentPath = () => window.location.pathname.replace(/\/+$/, '') || '/';
  const isShopAll = () => currentPath() === shopAllPath;

  const setupShopAllAtmosphere = () => {
    if (!isShopAll()) return;

    document.body.classList.add('lurxecare-shop-all');

    const main = document.querySelector('#MainContent');
    if (!main) return;

    let sky = main.querySelector('.lurxecare-shop-all-sky');
    if (!sky) {
      sky = document.createElement('div');
      sky.className = 'lurxecare-shop-all-sky';
      sky.setAttribute('aria-hidden', 'true');
      main.prepend(sky);
    }

    if (sky.dataset.ambientVersion === 'micro') return;
    sky.dataset.ambientVersion = 'micro';
    sky.replaceChildren();

    for (let index = 0; index < 10; index += 1) {
      const dust = document.createElement('i');
      dust.style.setProperty('--x', `${8 + ((index * 37) % 86)}%`);
      dust.style.setProperty('--y', `${7 + ((index * 53) % 88)}%`);
      dust.style.setProperty('--delay', `${-((index * 2.71) % 24).toFixed(2)}s`);
      dust.style.setProperty('--duration', `${(25 + (index % 4) * 3.2).toFixed(2)}s`);
      dust.style.setProperty('--size', `${1.5 + (index % 3) * 0.65}px`);
      dust.style.setProperty('--drift-x', `${-9 + (index % 5) * 4}px`);
      dust.style.setProperty('--drift-y', `${-7 + (index % 4) * 4}px`);
      sky.append(dust);
    }
  };

  const revealCard = (card) => {
    card.classList.add('is-lurxecare-visible');
    revealObserver?.unobserve(card);
  };

  const getRevealObserver = () => {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) revealCard(entry.target);
        });
      },
      { rootMargin: '80px 0px', threshold: 0.08 }
    );

    return revealObserver;
  };

  const setupShopAllCards = (scope = document) => {
    if (!isShopAll() || !(scope instanceof Element || scope instanceof Document || scope instanceof DocumentFragment)) {
      return;
    }

    const cards = [];
    if (scope instanceof Element && scope.matches('.product-grid__item')) cards.push(scope);
    scope.querySelectorAll?.('.product-grid__item').forEach((card) => cards.push(card));

    cards.forEach((card) => {
      if (card.dataset.lurxecareRevealBound === 'true') return;
      card.dataset.lurxecareRevealBound = 'true';
      card.classList.add('lurxecare-shop-card-reveal');
      card.style.setProperty('--lurxecare-reveal-delay', `${(revealSequence % 6) * 42}ms`);
      revealSequence += 1;

      if (reduceMotion.matches) {
        revealCard(card);
      } else {
        const observer = getRevealObserver();
        if (observer) observer.observe(card);
        else revealCard(card);
      }
    });
  };

  const addCollectionLinks = () => {
    if (collectionEntries.length === 0) return;
    document.querySelectorAll('.menu-drawer__menu-item-text').forEach((label) => {
      if (label.textContent.trim().toLowerCase() !== 'collections') return;

      const trigger = label.closest('.menu-drawer__menu-item');
      const host = trigger?.closest('details') ?? trigger?.closest('.menu-drawer__list-item');
      const list = host?.querySelector('.menu-drawer__menu--childlist');
      if (!list) return;

      const templateLink = list.querySelector('a.menu-drawer__menu-item');

      collectionEntries.forEach(({ href, label: textLabel }) => {
        const exists = [...list.querySelectorAll('a[href]')].some((anchor) => {
          const anchorPath = new URL(anchor.href, window.location.origin).pathname.replace(/\/+$/, '');
          return anchorPath === href;
        });
        if (exists) return;

        const item = document.createElement('li');
        item.className = 'menu-drawer__list-item';

        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.className = templateLink?.className || 'menu-drawer__menu-item menu-drawer__menu-item--child';

        const text = document.createElement('span');
        text.className = 'menu-drawer__menu-item-text wrap-text';
        text.textContent = textLabel;

        anchor.append(text);
        item.append(anchor);
        list.append(item);
      });
    });
  };

  const visibleCartIcon = () =>
    [...document.querySelectorAll('cart-icon[data-testid="cart-icon"], .header-actions__cart-icon')].find((icon) => {
      const rect = icon.getBoundingClientRect();
      const style = window.getComputedStyle(icon);
      return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
    });

  const ensureCelebrationLayer = () => {
    let layer = document.querySelector('.lurxecare-cart-star-trail');
    if (layer) return layer;

    layer = document.createElement('div');
    layer.className = 'lurxecare-cart-star-trail';
    layer.setAttribute('aria-hidden', 'true');
    document.body.append(layer);
    return layer;
  };

  const showCartConfirmation = (destinationRect) => {
    let confirmation = document.querySelector('.lurxecare-cart-confirmation');
    if (!confirmation) {
      confirmation = document.createElement('div');
      confirmation.className = 'lurxecare-cart-confirmation';
      // The theme already announces successful cart changes. Keep this extra
      // confirmation visual-only so screen readers do not hear a duplicate.
      confirmation.setAttribute('aria-hidden', 'true');
      confirmation.innerHTML = '<span aria-hidden="true">✦</span><strong></strong>';
      document.body.append(confirmation);
    }

    const edge = Math.min(96, Math.max(70, window.innerWidth * 0.2));
    const centerX = Math.min(window.innerWidth - edge, Math.max(edge, destinationRect.left + destinationRect.width / 2));
    const below = destinationRect.bottom + 14;
    const top = below < window.innerHeight - 72 ? below : Math.max(14, destinationRect.top - 54);
    confirmation.style.setProperty('--lurxecare-confirm-x', `${centerX}px`);
    confirmation.style.setProperty('--lurxecare-confirm-y', `${top}px`);

    const message = confirmation.querySelector('strong');
    if (message) {
      message.textContent = '';
      window.requestAnimationFrame(() => {
        message.textContent = 'Added to your ritual';
      });
    }

    confirmation.classList.remove('is-lurxecare-visible');
    window.requestAnimationFrame(() => confirmation.classList.add('is-lurxecare-visible'));

    if (confirmationTimer) window.clearTimeout(confirmationTimer);
    confirmationTimer = window.setTimeout(() => confirmation.classList.remove('is-lurxecare-visible'), 1850);
  };

  const celebrateCartAdd = (sourceRect) => {
    const cartIcon = visibleCartIcon();
    if (!cartIcon) return;

    const destinationRect = cartIcon.getBoundingClientRect();
    showCartConfirmation(destinationRect);

    if (reduceMotion.matches) return;

    const layer = ensureCelebrationLayer();
    layer.replaceChildren();

    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    const endX = destinationRect.left + destinationRect.width / 2;
    const endY = destinationRect.top + destinationRect.height / 2;
    const distance = Math.hypot(endX - startX, endY - startY);
    const arc = Math.min(88, Math.max(34, distance * 0.1));
    const starCount = window.innerWidth < 750 ? 5 : 7;

    for (let index = 0; index < starCount; index += 1) {
      const progressOffset = index / Math.max(1, starCount - 1);
      const jitterX = (index % 2 === 0 ? -1 : 1) * (3 + progressOffset * 4);
      const jitterY = ((index % 3) - 1) * 3;
      const starStartX = startX + jitterX;
      const starStartY = startY + jitterY;
      const starEndX = endX + (index - (starCount - 1) / 2) * 1.4;
      const starEndY = endY + ((index % 2) - 0.5) * 3;
      const middleX = starStartX + (starEndX - starStartX) * (0.5 + progressOffset * 0.035);
      const middleY = starStartY + (starEndY - starStartY) * 0.43 - arc;

      const star = document.createElement('i');
      star.style.setProperty('--lurxecare-star-start-x', `${starStartX}px`);
      star.style.setProperty('--lurxecare-star-start-y', `${starStartY}px`);
      star.style.setProperty('--lurxecare-star-middle-x', `${middleX}px`);
      star.style.setProperty('--lurxecare-star-middle-y', `${middleY}px`);
      star.style.setProperty('--lurxecare-star-end-x', `${starEndX}px`);
      star.style.setProperty('--lurxecare-star-end-y', `${starEndY}px`);
      star.style.setProperty('--lurxecare-star-delay', `${index * 24}ms`);
      star.style.setProperty('--lurxecare-star-size', `${4.5 + (index % 3) * 1.4}px`);
      layer.append(star);
    }

    cartIcon.classList.remove('is-lurxecare-cart-arrival');
    window.requestAnimationFrame(() => cartIcon.classList.add('is-lurxecare-cart-arrival'));
    window.setTimeout(() => cartIcon.classList.remove('is-lurxecare-cart-arrival'), 760);
    window.setTimeout(() => layer.replaceChildren(), 900);
  };

  const getAddButton = (event) => {
    const isUsableAddButton = (button) => {
      if (!(button instanceof HTMLButtonElement) || button.disabled || !button.matches('button[name="add"]')) return false;
      const rect = button.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const active = document.activeElement;
    if (isUsableAddButton(active)) return active;

    const eventTarget = event.target instanceof Element ? event.target : null;
    if (isUsableAddButton(eventTarget)) return eventTarget;

    return [...(eventTarget?.querySelectorAll('button[name="add"]') ?? [])].find(isUsableAddButton) ?? null;
  };

  const rememberAddTrigger = (event) => {
    // This runs on pointer/keyboard intent, not click. The synthetic click used
    // by the sticky bar therefore cannot replace the visible origin.
    if (!(event.target instanceof Element)) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

    const button = event.target.closest(
      'button[name="add"], .sticky-add-to-cart__button, [data-testid="standalone-add-to-cart"]'
    );
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const rect = button.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    latestAddTrigger = {
      rect: {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
      time: Date.now(),
    };
  };

  const getAddSourceRect = (event) => {
    const remembered = latestAddTrigger;
    latestAddTrigger = undefined;

    if (remembered && Date.now() - remembered.time < 5000) return remembered.rect;

    const sourceButton = getAddButton(event);
    if (!sourceButton) return null;

    const rect = sourceButton.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return rect;
  };

  const handledCartEvents = new WeakSet();
  const handleCartLinesUpdate = (event) => {
    if (handledCartEvents.has(event) || event.action !== 'add' || typeof event.promise?.then !== 'function') return;
    handledCartEvents.add(event);

    const sourceRect = getAddSourceRect(event);
    if (!sourceRect) return;

    event.promise
      .then((result) => {
        if (
          result?.detail?.didError ||
          result?.detail?.userErrors?.length ||
          result?.detail?.errors?.length ||
          result?.userErrors?.length
        ) {
          return;
        }
        window.requestAnimationFrame(() => celebrateCartAdd(sourceRect));
      })
      .catch(() => {
        // The theme's existing cart error handling remains the source of truth.
      });
  };

  const watchShopAllCards = () => {
    if (!isShopAll()) return;
    const main = document.querySelector('#MainContent');
    if (!main || main === cardsMutationRoot) return;

    cardsMutationObserver?.disconnect();
    cardsMutationRoot = main;
    cardsMutationObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element || node instanceof DocumentFragment)) return;
          if (node.matches?.('.product-grid__item') || node.querySelector?.('.product-grid__item')) {
            setupShopAllCards(node);
          }
        });
      });
    });
    cardsMutationObserver.observe(main, { childList: true, subtree: true });
  };

  const setup = (scope = document) => {
    setupShopAllAtmosphere();
    setupShopAllCards(scope);
    addCollectionLinks();
    watchShopAllCards();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup(document), { once: true });
  } else {
    setup(document);
  }

  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  // Capture the visible trigger before the theme's click handler can proxy a
  // sticky-bar click to the off-screen product form.
  document.addEventListener('pointerdown', rememberAddTrigger, true);
  document.addEventListener('keydown', rememberAddTrigger, true);
  document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);
  window.addEventListener('pageshow', () => setup(document));

})();
